import { Box } from '@mui/material'
import { buildInterpolatedSeries } from 'components/shared/processes/chartNoDataSeries'
import moment from 'moment'
import { MouseEvent, useEffect, useMemo, useRef } from 'react'
import ReactApexChart from 'react-apexcharts'
import { COLORS } from 'styles/colors'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { localizationApexCharts } from 'utils'
import 'components/group/styles/module.ApexChartTooltip.css'
import EmptyChartPlot from './EmptyChartPlot'
import { PushPoint } from './nudgeMetrics'
import { placeTip, pointOnPlot } from './plotHover'

const PushesTrendCard = ({
	points,
	manualTooltip = false,
}: {
	points: PushPoint[]
	manualTooltip?: boolean
}) => {
	const categories = points.map(point => point.date)
	const values = points.map(point => point.count)
	const noData = buildInterpolatedSeries(values)
	const hasValues = values.some(value => value != null)
	const singlePoint = points.length === 1
	const peak = values.reduce(
		(max, value) => (value != null && value > max ? value : max),
		0,
	)
	const rangeStart = points[0]
		? moment(points[0].date).startOf('day').valueOf()
		: moment().startOf('day').valueOf()
	const rangeEnd = points[points.length - 1]
		? moment(points[points.length - 1].date)
				.endOf('day')
				.valueOf()
		: moment().endOf('day').valueOf()
	const options = useMemo(
		() => ({
			chart: {
				type: 'line' as const,
				toolbar: { show: false },
				zoom: { enabled: false },
				animations: { enabled: false },
				connectNulls: false,
				locales: localizationApexCharts(),
				defaultLocale: 'ru',
				fontFamily: 'Montserrat',
			},
			stroke: { width: 3, curve: 'straight' as const },
			colors: [COLORS.inputBorder, COLORS.health],
			markers: {
				size: 0,
				colors: ['#fff'],
				strokeColors: [COLORS.health, COLORS.inputBorder],
				strokeWidth: 2,
				hover: { size: 6 },
			},
			dataLabels: { enabled: false },
			grid: {
				strokeDashArray: 6,
				show: true,
				borderColor: '#E6E6E8',
				xaxis: {
					lines: {
						show: true,
					},
				},
			},
			legend: { show: false },
			xaxis: {
				categories,
				labels: {
					formatter: (label: string) =>
						label ? moment(label).format('DD.MM') : '',
					style: {
						fontSize: '12px',
						fontFamily: 'Montserrat',
						colors: '#7F7F84',
					},
				},
				tooltip: { enabled: false },
			},
			yaxis: {
				min: 0,
				max: singlePoint && peak > 0 ? peak * 1.35 : undefined,
				tickAmount: 4,
				labels: {
					style: {
						fontSize: '12px',
						fontFamily: 'Montserrat',
						colors: '#7F7F84',
					},
					formatter: (amount: number) => Math.round(amount).toString(),
				},
			},
			tooltip: {
				enabled: !manualTooltip,
				custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
					const point = points[dataPointIndex]
					const date = moment(point?.date).format('DD.MM.YY')
					if (!point || point.count == null) {
						return `<div style="padding:12px;background:#fff;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.12)">
							<div style="font-size:12px;color:#7F7F84">Нет данных</div>
							<div style="margin-top:4px;font-size:12px;color:#7F7F84">${date}</div>
						</div>`
					}
					return `<div style="padding:12px;background:#fff;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.12)">
						<div style="display:flex;align-items:center;gap:6px">
							<span style="width:10px;height:10px;border-radius:50%;background:${COLORS.health}"></span>
							<span style="font-size:12px;color:#7F7F84">Подталкиваний</span>
							<b style="font-size:16px">${point.count}</b>
							<span style="font-size:12px;color:#7F7F84">раз/день</span>
						</div>
						<div style="margin-top:4px;font-size:12px;color:#7F7F84">${date}</div>
					</div>`
				},
			},
		}),
		[categories, manualTooltip, peak, points, singlePoint],
	)
	const tipRef = useRef<HTMLDivElement>(null)
	const dotRef = useRef<HTMLDivElement>(null)
	const hideTip = () => {
		if (tipRef.current) tipRef.current.style.display = 'none'
		if (dotRef.current) dotRef.current.style.display = 'none'
	}
	useEffect(() => {
		if (!manualTooltip) return
		const onMove = (event: PointerEvent) => {
			const root = document.getElementById('feedingPushesChart')
			if (!root || !tipRef.current || tipRef.current.style.display === 'none')
				return
			const grid = root.querySelector('.apexcharts-grid')
			const box = (grid ?? root).getBoundingClientRect()
			const inside =
				event.clientX >= box.left &&
				event.clientX <= box.right &&
				event.clientY >= box.top &&
				event.clientY <= box.bottom
			if (!inside) {
				if (tipRef.current) tipRef.current.style.display = 'none'
				if (dotRef.current) dotRef.current.style.display = 'none'
			}
		}
		window.addEventListener('pointermove', onMove)
		return () => window.removeEventListener('pointermove', onMove)
	}, [manualTooltip])
	const showTip = (event: MouseEvent<HTMLDivElement>) => {
		const tip = tipRef.current
		if (!tip) return
		const hit = pointOnPlot(
			event.currentTarget,
			event.clientX,
			event.clientY,
			points.length,
			null,
		)
		if (!hit) {
			hideTip()
			return
		}
		const point = points[hit.index]
		const drawn = point?.count ?? noData[hit.index]
		const placed = pointOnPlot(
			event.currentTarget,
			event.clientX,
			event.clientY,
			points.length,
			typeof drawn === 'number' ? drawn : null,
		)
		if (!placed) {
			hideTip()
			return
		}
		const date = moment(point?.date).format('DD.MM.YY')
		tip.innerHTML =
			!point || point.count == null
				? `<div style="padding:12px;background:#fff;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.12)">
						<div style="font-size:12px;color:#7F7F84">Нет данных</div>
						<div style="margin-top:4px;font-size:12px;color:#7F7F84">${date}</div>
					</div>`
				: `<div style="padding:12px;background:#fff;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.12)">
						<div style="display:flex;align-items:center;gap:6px">
							<span style="width:10px;height:10px;border-radius:50%;background:${COLORS.health}"></span>
							<span style="font-size:12px;color:#7F7F84">Подталкиваний</span>
							<b style="font-size:16px">${point.count}</b>
							<span style="font-size:12px;color:#7F7F84">раз/день</span>
						</div>
						<div style="margin-top:4px;font-size:12px;color:#7F7F84">${date}</div>
					</div>`
		if (dotRef.current) {
			dotRef.current.style.borderColor =
				point?.count == null ? COLORS.inputBorder : COLORS.health
		}
		placeTip(event.currentTarget, tip, dotRef.current, placed.x, placed.y)
	}

	return (
		<Box
			sx={{
				flex: '1 1 100%',
				width: '100%',
				height: '390px',
				p: '24px',
				borderRadius: '12px',
				backgroundColor: COLORS.white,
				boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
				boxSizing: 'border-box',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'visible',
			}}
		>
			<Box
				sx={{
					fontSize: '20px',
					fontWeight: 700,
					lineHeight: '24px',
					color: COLORS.black,
				}}
			>
				Подталкивания
			</Box>
			<Box sx={{ mt: '16px', mb: '4px' }}>
				<TypographySecondary sx={{ display: 'block', lineHeight: '15px' }}>
					Количество
				</TypographySecondary>
				<TypographySecondary sx={{ display: 'block', lineHeight: '15px' }}>
					подталкиваний
				</TypographySecondary>
			</Box>
			<Box
				id='feedingPushesChart'
				onMouseMove={manualTooltip ? showTip : undefined}
				onMouseLeave={manualTooltip ? hideTip : undefined}
				sx={{
					position: 'relative',
					flex: 1,
					minHeight: 0,
					overflow: 'visible',
					'& .apexcharts-tooltip': {
						zIndex: 20,
						overflow: 'visible !important',
						border: 'none !important',
						background: 'transparent !important',
						boxShadow: 'none !important',
					},
					'& .apexcharts-gridlines-vertical line': {
						strokeDasharray: '0 !important',
					},
				}}
			>
				{hasValues ? (
					<ReactApexChart
						type='line'
						height={250}
						series={[
							{
								name: 'Подталкивания',
								data: values,
								color: COLORS.health,
								zIndex: 2,
							},
							{
								name: 'Нет данных',
								data: noData,
								color: COLORS.inputBorder,
								zIndex: 1,
							},
						]}
						options={options}
					/>
				) : (
					<EmptyChartPlot start={rangeStart} end={rangeEnd} />
				)}
				<div
					ref={dotRef}
					style={{
						display: 'none',
						position: 'absolute',
						width: 12,
						height: 12,
						marginLeft: -6,
						marginTop: -6,
						borderRadius: '50%',
						background: '#fff',
						border: `2px solid ${COLORS.health}`,
						pointerEvents: 'none',
						zIndex: 21,
					}}
				/>
				<div
					ref={tipRef}
					style={{
						display: 'none',
						position: 'absolute',
						zIndex: 22,
						pointerEvents: 'none',
					}}
				/>
			</Box>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<Box
					sx={{
						width: '12px',
						height: '12px',
						borderRadius: '50%',
						backgroundColor: COLORS.health,
					}}
				/>
				<TypographySecondary sx={{ ml: '8px', mr: '16px' }}>
					Подталкивания
				</TypographySecondary>
				<Box
					sx={{
						width: '12px',
						height: '12px',
						borderRadius: '50%',
						backgroundColor: COLORS.inputBorder,
					}}
				/>
				<TypographySecondary sx={{ ml: '8px' }}>Нет данных</TypographySecondary>
			</Box>
		</Box>
	)
}

export default PushesTrendCard
